import {
  BulkAdjustmentTriggerContext,
  CategorizeClientTransactionsTriggerContext,
  CollectAllLedgerStatementsJobParameters,
  CollectAllLedgerStatementsTriggerContext,
  CollectMonthlyStatementJobParameters,
  JobType,
  ResolveBalanceCheckerJobParameters,
  ResolveBulkAdjustmentJobParameters,
  ResolveDocumentAdjustmentJobParameters,
  ResolveIncorrectAdjustmentJobParameters,
  ResolveIncorrectAdjustmentTriggerContext,
  ResolveIncorrectBalanceJobParameters,
  ResolveIncorrectBalanceTriggerContext,
  ResolveIncorrectBooksJobParameters,
  ResolveIncorrectBooksTriggerContext,
  ResolveIncorrectCategorizationJobParameters,
  ResolveIncorrectCategorizationTriggerContext,
  ResolvePlaidConnectionJobParameters,
  ResolveThirdPartyLoginAdjustmentJobParameters,
  ReviewGeneralLedgerTriggerContext,
  SetupNewConnectionTriggerContext,
  Task,
  TriageClientDocumentsTriggerContext,
} from './types';
import moment from 'moment/moment';
import formatCurrency from '../format-currency';
import { differenceInCalendarMonths, format, isSameYear, parse } from 'date-fns';

const ADDON_NOT_FOUND = '[AddOn Not Found]';
export const LEDGER_NOT_FOUND = '[Ledger Not Found]';

export type JobDetailsBuilder = (task: Task) => string | null;

export const jobDetailsLookup: {
  [key in JobType]?: JobDetailsBuilder;
} = {
  [JobType.RESOLVE_BALANCE_CHECKER]: task => {
    return (
      (task.job.parameters as ResolveBalanceCheckerJobParameters)?.ledgerAccount?.name ??
      LEDGER_NOT_FOUND
    );
  },
  [JobType.RESOLVE_PLAID_CONNECTION]: task => {
    return (
      (task.job.parameters as ResolvePlaidConnectionJobParameters)?.ledgerAccount?.name ??
      LEDGER_NOT_FOUND
    );
  },
  [JobType.RESOLVE_DOCUMENT_ADJUSTMENT]: task => {
    const params = task.job.parameters as ResolveDocumentAdjustmentJobParameters;
    const addOnName = params?.addOn?.name ?? ADDON_NOT_FOUND;
    const documentName = params?.document?.name;
    const documentDate = params?.document?.date;
    return documentDate ? `${documentDate}, ${addOnName}, Doc Name: ${documentName}` : addOnName;
  },
  [JobType.RESOLVE_3RD_PARTY_LOGIN_ADJUSTMENT]: task => {
    const params = task.job.parameters as ResolveThirdPartyLoginAdjustmentJobParameters;
    return `${params?.yearMonth}, ${params?.addOn?.name ?? ADDON_NOT_FOUND}`;
  },
  [JobType.RESOLVE_BULK_ADJUSTMENT]: task => {
    const params = task.job.parameters as ResolveBulkAdjustmentJobParameters;
    const triggerContext = task.job.triggerContext as BulkAdjustmentTriggerContext;
    let details = '';
    details += formatDateRange(triggerContext);
    details += `${triggerContext?.startDate ? ', ' : ''}${params?.addOn?.name ?? ADDON_NOT_FOUND}`;
    return details;
  },
  [JobType.CATEGORIZE_CLIENT_TRANSACTIONS]: task => {
    return formatCategorizeClientTransactionsDetails(task);
  },
  [JobType.TRIAGE_CLIENT_DOCUMENTS]: task => {
    const triggerContext = task.job.triggerContext as TriageClientDocumentsTriggerContext;
    const numUnknownDocs = triggerContext?.numUnknownDocs;
    return numUnknownDocs ? `Approx #: ${numUnknownDocs}` : '';
  },
  [JobType.COLLECT_MONTHLY_STATEMENT]: task => {
    return formatCollectMonthlyStatementDetails(task);
  },
  [JobType.COLLECT_ALL_LEDGER_STATEMENTS]: task => {
    const params = task.job.parameters as CollectAllLedgerStatementsJobParameters;
    const triggerContext = task.job?.triggerContext as CollectAllLedgerStatementsTriggerContext;
    return params?.ledgerAccount?.name
      ? `${params?.ledgerAccount?.name} (${formatDateActiveAsNumMonths(
          triggerContext?.ledgerDateActive,
        )})`
      : LEDGER_NOT_FOUND;
  },
  [JobType.REVIEW_GENERAL_LEDGER]: task => {
    const triggerContext = task.job.triggerContext as ReviewGeneralLedgerTriggerContext;
    return `Scope: ${triggerContext?.startDate} - ${triggerContext?.endDate}`;
  },
  [JobType.RESOLVE_INCORRECT_ADJUSTMENT]: task => {
    const params = task.job.parameters as ResolveIncorrectAdjustmentJobParameters;
    const triggerContext = task.job.triggerContext as ResolveIncorrectAdjustmentTriggerContext;
    return triggerContext?.startDate
      ? formatDateRange({
          ...triggerContext,
          numMonthsAdjustmentRequired: triggerContext.numMonths,
        })
      : `Review Period: ${params?.reviewPeriod}`;
  },
  [JobType.RESOLVE_INCORRECT_BALANCE]: task => {
    const params = task.job.parameters as ResolveIncorrectBalanceJobParameters;
    const triggerContext = task.job.triggerContext as ResolveIncorrectBalanceTriggerContext;
    return triggerContext?.startDate
      ? formatDateRange({
          ...triggerContext,
          numMonthsAdjustmentRequired: triggerContext.numMonths,
        })
      : `Review Period: ${params?.reviewPeriod}`;
  },
  [JobType.RESOLVE_INCORRECT_CATEGORIZATION]: task => {
    const params = task.job.parameters as ResolveIncorrectCategorizationJobParameters;
    const triggerContext = task.job.triggerContext as ResolveIncorrectCategorizationTriggerContext;
    return triggerContext?.startDate
      ? formatDateRange({
          ...triggerContext,
          numMonthsAdjustmentRequired: triggerContext.numMonths,
        })
      : `Review Period: ${params?.reviewPeriod}`;
  },
  [JobType.RESOLVE_INCORRECT_BOOKS]: task => {
    const params = task.job.parameters as ResolveIncorrectBooksJobParameters;
    const triggerContext = task.job.triggerContext as ResolveIncorrectBooksTriggerContext;
    return triggerContext?.startDate
      ? formatDateRange({
          ...triggerContext,
          numMonthsAdjustmentRequired: triggerContext.numMonths,
        })
      : `Review Period: ${params?.reviewPeriod}`;
  },
  [JobType.SETUP_NEW_CONNECTION]: task => {
    const triggerContext = task.job.triggerContext as SetupNewConnectionTriggerContext;
    const source = triggerContext?.source || 'Unknown';
    const institution = triggerContext?.institution || 'Unknown';
    const numAccounts = triggerContext?.numAccounts || 0;
    return `Source: ${source} Institution: ${institution} Accounts: ${numAccounts}`;
  },
};

function formatCollectMonthlyStatementDetails(task: Task): string {
  const params = task.job.parameters as CollectMonthlyStatementJobParameters;
  let details = '';
  details += params?.ledgerAccount?.name ?? LEDGER_NOT_FOUND;
  details += params?.statementMonth ? `, ${params?.statementMonth}` : '';
  return details;
}

function formatCategorizeClientTransactionsDetails(task: Task): string {
  const triggerContext = task.job.triggerContext as CategorizeClientTransactionsTriggerContext;
  let details = '';
  details += formatNotesText(triggerContext);
  details += formatOldestDateText(details, triggerContext);
  details += formatNumAwaitingCategoryText(details, triggerContext);
  details += formatTotalAmountAwaitingCategoryText(details, triggerContext);
  return details;
}

function formatNotesText(triggerContext: CategorizeClientTransactionsTriggerContext): string {
  return triggerContext?.numNeedsReview > 0 ? 'Notes received' : '';
}

function formatOldestDateText(
  details: string,
  triggerContext: CategorizeClientTransactionsTriggerContext,
): string {
  let text = '';
  if (triggerContext?.oldestTransactionDate) {
    const oldestDateStr = triggerContext?.oldestTransactionDate;
    const oldestDate = new Date(oldestDateStr);
    const daysOld = moment().diff(oldestDate, 'days');
    const oldestTransaction = moment(oldestDate).utc().format('MMMM YYYY');
    if (daysOld > 14) {
      if (details) text = ', ';
      text = text + `Oldest: ${oldestTransaction}`;
    }
  }
  return text;
}

function formatNumAwaitingCategoryText(
  details: string,
  triggerContext: CategorizeClientTransactionsTriggerContext,
) {
  const num = triggerContext?.numAwaitingCategory;
  const prefix = details ? ', ' : '';
  return num != null && num > 0 ? prefix + `Approx #: ${num}` : '';
}

function formatTotalAmountAwaitingCategoryText(
  details: string,
  triggerContext: CategorizeClientTransactionsTriggerContext,
) {
  const total = triggerContext?.totalAmountAwaitingCategory;
  const prefix = details ? ', ' : '';
  return total != null && total > 0 ? prefix + `ACE/R Total: ${formatCurrency(total)}` : '';
}

function formatDateRange(triggerContext: {
  startDate: string;
  endDate: string;
  numMonthsAdjustmentRequired: number;
}): string {
  if (!triggerContext?.startDate || !triggerContext?.endDate) return '';
  const start = parse(triggerContext.startDate);
  const end = parse(triggerContext.endDate);
  const sameYear = isSameYear(start, end);
  const startString = format(start, sameYear ? 'MMM' : 'MMM YYYY');
  const endString = format(end, 'MMM YYYY');
  return `${startString} - ${endString} (${triggerContext.numMonthsAdjustmentRequired} months)`;
}

function formatDateActiveAsNumMonths(ledgerDateActive: string): string {
  const date = new Date(ledgerDateActive);
  const now = new Date();
  const diff = differenceInCalendarMonths(now, date);
  return `${diff} months`;
}
